import { GradientBorder, ProcessingSpinner } from "@storytell/chat-ui/ui";
import {
  TbArrowLeft,
  TbArrowUp,
  TbCheck,
  TbChevronRight,
  TbCircleCheck,
  TbCube,
  TbDots,
  TbExternalLink,
  TbFilter,
  TbHistory,
  TbLayoutSidebar,
  TbLayoutSidebarRightCollapse,
  TbMessage2,
  TbMessageCircleQuestion,
  TbPlayerPauseFilled,
  TbPlayerStopFilled,
  TbPlus,
  TbSearch,
  TbShare3,
  TbTag,
  TbUserPlus,
  TbUsers,
  TbX,
} from "solid-icons/tb";
import { type Component, For, type JSX, Match, type ParentComponent, Show, Switch } from "solid-js";
import { useI18n } from "~/i18n";
import type { TranslationKey } from "~/i18n/translations";
import { at, Crown, type Person } from "./parts";

/* The desktop app's window, panels and controls, copied from its source so the
   mockups draw what the app draws. Everything here renders inside `.mk-app`,
   whose skin in mockups.css carries the app's type scale, radii and fonts. */

// A control present but quieter than the one decision on its row.
export const QUIET_BUTTON =
  "inline-flex h-6 shrink-0 items-center gap-1.5 rounded-md px-2.5 text-xs whitespace-nowrap text-muted-text";

// The transcript's speaker accents: the local mic reads gold, everyone else blue.
export const YOU_COLOR = "#d4a017";
export const THEM_COLOR = "#7088e8";

export type TranscriptEntry = { speaker: string; local?: boolean; time: string; text: string; appearAt?: number };

export const TranscriptBlock: Component<{ entry: TranscriptEntry; class?: string }> = (props) => (
  <div
    class={`mb-3 ${props.entry.appearAt === undefined ? "" : "msg-in"} ${props.class ?? ""}`}
    style={at(props.entry.appearAt ?? 0)}
  >
    <div class="mb-1 flex items-baseline gap-2">
      <span
        class="min-w-0 truncate text-xs font-semibold"
        style={{ color: props.entry.local ? YOU_COLOR : THEM_COLOR }}
      >
        {props.entry.speaker}
      </span>
      <span class="shrink-0 text-xs text-muted-text tabular-nums">{props.entry.time}</span>
    </div>
    <p class="max-w-[72ch] text-sm leading-relaxed text-primary-text">{props.entry.text}</p>
  </div>
);

// Read as UTC both on the server and in the browser, so the render hydrates.
const UTC_DATE = { timeZone: "UTC" } as const;

/** A session's start, as the toolbar states it for a session not started today. */
export const startedLabel = (iso: string, locale: string): string =>
  new Date(`${iso}Z`).toLocaleString(locale, {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
    ...UTC_DATE,
  });

const TrafficLights = () => (
  <span class="absolute top-1/2 left-5 flex -translate-y-1/2 gap-2">
    <span class="size-3 rounded-full bg-mock-close" />
    <span class="size-3 rounded-full bg-mock-minimize" />
    <span class="size-3 rounded-full bg-mock-zoom" />
  </span>
);

const RAIL_BUTTON = "grid size-9 place-items-center rounded-md text-muted-text";

/** A label that changes at `swapAt`: the first leaves as the second takes its place. */
export const Swap: Component<{ before: JSX.Element; after: JSX.Element; swapAt: number; class?: string }> = (props) => (
  <span class={`grid ${props.class ?? ""}`}>
    <span class="exit col-start-1 row-start-1" style={at(props.swapAt)}>
      {props.before}
    </span>
    <span class="card-appear col-start-1 row-start-1" style={at(props.swapAt)}>
      {props.after}
    </span>
  </span>
);

/** The who's-here stack: gold-tinted initials, the host ringed, and the overflow count. */
export const PeopleStack: Component<{ people: Person[]; host?: Person; more?: number; joinAt?: number }> = (props) => (
  <span class="mac-control flex items-center rounded-md px-1.5">
    <span class="flex items-center -space-x-1.5">
      <For each={props.people}>
        {(person, i) => (
          <span
            class="relative grid size-5 place-items-center rounded-full border border-panel text-[0.625rem] font-semibold text-accent"
            classList={{
              "ring-1 ring-host": person === props.host,
              "pop-in": props.joinAt !== undefined && i() === props.people.length - 1,
            }}
            style={{
              "z-index": props.people.length - i(),
              "background-color": "color-mix(in srgb, var(--color-accent) 20%, var(--color-panel))",
              ...(props.joinAt !== undefined && i() === props.people.length - 1 ? at(props.joinAt) : {}),
            }}
          >
            {person.name[0]}
          </span>
        )}
      </For>
    </span>
    <Show when={props.more}>
      <span class="relative -ms-1.5 flex h-5 min-w-6 items-center justify-center">
        <span class="absolute inset-y-0 -inset-s-2 inset-e-0 rounded-full bg-linear-to-r from-transparent via-tile/90 to-tile" />
        <span class="relative ps-1 text-[0.625rem] font-semibold text-secondary-text">+{props.more}</span>
      </span>
    </Show>
  </span>
);

/** The session controls' bar once the call has ended: use this Live Session, Chat, a new Live Session. */
export const EndedBar: Component<{ class?: string }> = (props) => {
  const { t } = useI18n();
  return (
    <div class={`flex max-w-full shrink-0 flex-wrap items-center justify-center gap-1 p-1.5 ${props.class ?? ""}`}>
      <span class="mac-control flex shrink-0 items-center gap-1.5 rounded-md px-2.5 font-semibold text-secondary-text">
        <TbShare3 class="size-3.5" aria-hidden="true" /> {t("marketing.mock.sheet.use")}
      </span>
      <span class="mac-control flex shrink-0 items-center gap-1.5 rounded-md px-2.5 font-semibold text-secondary-text">
        <TbMessage2 class="size-3.5" aria-hidden="true" /> {t("marketing.mock.ask.chat")}
      </span>
      <span class="mx-0.5 h-3.5 w-px bg-primary-border" />
      <span class="mac-control flex shrink-0 items-center gap-1.5 rounded-md px-2.5 font-semibold text-secondary-text">
        <TbPlus class="size-3.5" aria-hidden="true" /> {t("marketing.mock.sheet.new_session")}
      </span>
    </div>
  );
};

const BAR_HEIGHTS = [5, 9, 13, 16, 13, 9, 5, 6, 10, 14, 16, 14, 10, 6, 5];

/**
 * The floating session controls while a call records: the live clock, pause and
 * the waveform, who's here and Invite, Chat, and Stop. `people` slots in the
 * roster; `invite` is laid over the Invite button, for a cursor to press it.
 */
export const SessionPill: Component<{
  clock: string;
  people?: JSX.Element;
  invite?: JSX.Element;
  /** Off where the pill shares the window with the Chief pane and the waveform would push it past the work card. */
  wave?: boolean;
}> = (props) => {
  const { t } = useI18n();
  return (
    <div class="mac-pill pointer-events-auto flex w-max max-w-full items-center justify-center gap-1 overflow-hidden border border-primary-border bg-tile p-1.5 text-sm text-primary-text shadow-2xl">
      <span class="mac-control flex shrink-0 items-center gap-1.5 rounded-md px-2 font-semibold text-danger tabular-nums">
        <span class="size-2 rounded-full bg-danger" />
        {t("marketing.mock.live")}
        <span class="font-medium text-secondary-text">{props.clock}</span>
      </span>
      <span class="mac-control hidden shrink-0 items-center gap-1.5 rounded-md px-2 font-medium text-secondary-text @2xl:flex">
        <TbPlayerPauseFilled class="size-3.5" aria-hidden="true" />
        {t("marketing.mock.pill.pause")}
      </span>
      <Show when={props.wave ?? true}>
        <span class="mk-wave mac-control hidden items-center gap-0.75 px-1.5 @2xl:flex">
          <For each={BAR_HEIGHTS}>{(height) => <span style={{ height: `${height}px` }} />}</For>
        </span>
      </Show>
      <span class="mx-0.5 h-3.5 w-px bg-primary-border" />
      <span class="flex items-center">
        {props.people}
        <span class="mac-control relative flex shrink-0 items-center justify-center gap-1.5 rounded-md px-2.5 text-sm font-medium text-secondary-text">
          <TbUserPlus class="size-4" aria-hidden="true" />
          {t("marketing.mock.pill.invite")}
          {props.invite}
        </span>
      </span>
      <span class="mac-control hidden shrink-0 items-center gap-1.5 rounded-md px-2.5 font-semibold text-secondary-text @2xl:flex">
        <TbMessage2 class="size-3.5" aria-hidden="true" />
        {t("marketing.mock.ask.chat")}
      </span>
      <span class="mx-0.5 h-3.5 w-px bg-primary-border" />
      <span class="mac-control flex shrink-0 items-center gap-1.5 rounded-md px-2 font-semibold text-danger">
        <TbPlayerStopFilled class="size-3.5" aria-hidden="true" />
        {t("marketing.mock.pill.stop")}
      </span>
    </div>
  );
};

type SessionTab = "notes" | "transcript" | "documents" | "summary";

/** A view tab on the work card or the Chief pane; the open one carries the gold bar. */
export const ViewTab: ParentComponent<{ open?: boolean }> = (props) => (
  <span
    class="inline-flex h-6 shrink-0 items-center gap-1.5 rounded-md px-2.5 text-sm whitespace-nowrap"
    classList={{
      "rounded-b-none bg-muted-border font-semibold text-primary-text shadow-[inset_0_-2px_0_var(--color-accent)]":
        props.open,
      "text-muted-text": !props.open,
    }}
  >
    {props.children}
  </span>
);

// Keys rather than strings: a locale that loads after the window is drawn still renames the tabs.
const TABS: [SessionTab, TranslationKey][] = [
  ["notes", "marketing.mock.tab.notes"],
  ["transcript", "marketing.mock.transcript"],
  ["documents", "live_sessions.tabs.documents"],
  ["summary", "marketing.mock.tab.summary"],
];

/** One tab through a scene's view changes: `open[i]` is the tab open until `at[i]`. */
const TabStates: Component<{ id: SessionTab; label: string; open: SessionTab[]; at: number[] }> = (props) => (
  <Show
    when={props.at.length > 0 && props.open.includes(props.id)}
    fallback={<ViewTab open={props.open[0] === props.id}>{props.label}</ViewTab>}
  >
    <Swap
      swapAt={props.at[0]}
      before={<ViewTab open={props.open[0] === props.id}>{props.label}</ViewTab>}
      after={<TabStates id={props.id} label={props.label} open={props.open.slice(1)} at={props.at.slice(1)} />}
    />
  </Show>
);

const ICON = "grid size-6 shrink-0 place-items-center rounded-md text-muted-text";

/** The open sidebar's rail form: home, the toggle, the project, a new Live Session, tags and recents. */
const Rail: Component = () => (
  <aside class="hidden w-14 shrink-0 flex-col items-center gap-1 @2xl:flex">
    <span class="mt-2 mb-1 grid size-8 place-items-center">
      <Crown class="h-auto w-4.5" />
    </span>
    <span class={RAIL_BUTTON}>
      <TbLayoutSidebar class="size-4.5" stroke-width={1.5} aria-hidden="true" />
    </span>
    <span class={RAIL_BUTTON}>
      <TbCube class="size-4.5" stroke-width={1.5} aria-hidden="true" />
    </span>
    <span class="grid size-9 place-items-center rounded-md bg-brand">
      <span class="grid size-4 place-items-center rounded-full border-[1.5px] border-white">
        <span class="size-2 rounded-full bg-(--mac-danger-fill)" />
      </span>
    </span>
    <span class={RAIL_BUTTON}>
      <TbTag class="size-4.5" stroke-width={1.5} aria-hidden="true" />
    </span>
    <span class={RAIL_BUTTON}>
      <TbHistory class="size-4.5" stroke-width={1.5} aria-hidden="true" />
    </span>
  </aside>
);

/**
 * The Live Session window: the toolbar with the project, the session's title
 * and its facts, the rail, and the work card with its Notes, Transcript,
 * Documents and Summary tabs and the session controls at its foot. `pane` is
 * the Chief pane card beside it, laid out by the mockup; on a phone it stacks
 * under the work card.
 */
export const SessionWindow: ParentComponent<{
  project: string;
  title: string;
  facts: JSX.Element;
  live?: boolean;
  /** The second the call ends, for a scene that plays past it: the dot is live until then. */
  liveUntil?: number;
  tab: SessionTab;
  /** The tabs the window moves to, each at its `at`, in order, for a scene that changes view. */
  next?: { tab: SessionTab; at: number }[];
  bar: JSX.Element;
  pane?: JSX.Element;
}> = (props) => {
  const { t } = useI18n();
  return (
    <div class="mk-app flex h-full flex-col bg-(--mac-chrome-solid) text-primary-text">
      <div class="relative flex h-9 shrink-0 items-center gap-3 ps-21 pe-3">
        <TrafficLights />
        <span class="flex min-w-0 items-center gap-2 text-sm">
          <span class="hidden max-w-40 truncate text-muted-text @2xl:inline">{props.project}</span>
          <TbChevronRight
            class="hidden size-3.5 shrink-0 text-muted-text @2xl:block"
            stroke-width={1.5}
            aria-hidden="true"
          />
          <span class="min-w-0 truncate font-semibold">{props.title}</span>
          <TbDots class="size-4 shrink-0 text-muted-text" stroke-width={1.5} aria-hidden="true" />
        </span>
        <span class="hidden h-6 min-w-0 shrink items-center gap-1.5 rounded-md border border-primary-border px-2 text-xs whitespace-nowrap text-muted-text @2xl:flex">
          <span
            class="relative size-1.5 shrink-0 rounded-full"
            classList={{
              "bg-(--mac-danger)": props.live && props.liveUntil === undefined,
              "bg-muted-text": !props.live || props.liveUntil !== undefined,
            }}
          >
            <span
              class="absolute inset-0 rounded-full bg-(--mac-danger)"
              classList={{ exit: props.liveUntil !== undefined, hidden: props.liveUntil === undefined }}
              style={at(props.liveUntil ?? 0)}
            />
          </span>
          <span class="truncate">{props.facts}</span>
        </span>
        <span class="ms-auto flex shrink-0 items-center gap-3 text-muted-text">
          <span class="hidden h-6 w-44 items-center gap-2 rounded-md border border-primary-border bg-tile px-2.5 text-xs @2xl:flex">
            <TbSearch class="size-3.5 shrink-0" aria-hidden="true" />
            <span class="min-w-0 flex-1 truncate">{t("marketing.mock.search")}</span>
            <span>⌘K</span>
          </span>
          <TbShare3 class="size-4" stroke-width={1.5} aria-hidden="true" />
        </span>
      </div>
      <div class="flex min-h-0 flex-1">
        <Rail />
        <div class="flex min-w-0 flex-1 flex-col gap-2.5 bg-canvas ps-2.5 pt-2.5 @2xl:flex-row @2xl:ps-0">
          <div class="chief-card relative me-2.5 flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden @2xl:not-last:me-0">
            <div class="flex h-10 shrink-0 items-center gap-0.5 border-b border-(--cb-divider) px-2">
              <For each={TABS}>
                {([id, label]) => (
                  <TabStates
                    id={id}
                    label={t(label)}
                    open={[props.tab, ...(props.next ?? []).map((n) => n.tab)]}
                    at={(props.next ?? []).map((n) => n.at)}
                  />
                )}
              </For>
              <span class="ms-auto flex items-center gap-1">
                <span class={ICON}>
                  <TbUsers class="size-4" aria-hidden="true" />
                </span>
                <span class={ICON}>
                  <TbDots class="size-4" aria-hidden="true" />
                </span>
              </span>
            </div>
            <div class="mk-read relative min-h-0 flex-1 overflow-hidden">{props.children}</div>
            <div class="pointer-events-none absolute inset-x-0 bottom-3 flex justify-center px-3">{props.bar}</div>
          </div>
          {props.pane}
        </div>
      </div>
    </div>
  );
};

/**
 * The Chief pane: its Activity and Chats tabs, the open tab's body, and the
 * Ask Chief composer. `thread` opens a conversation at `at`, its header taking
 * the tabs' place.
 */
export const ChiefPane: ParentComponent<{ thread?: { title: string; at: number }; class?: string }> = (props) => {
  const { t } = useI18n();
  return (
    <div class={`chief-card me-2.5 flex min-h-0 min-w-0 flex-col overflow-hidden ${props.class ?? ""}`}>
      <div class="grid h-10 shrink-0 border-b border-(--cb-divider) px-2">
        <div
          class="col-start-1 row-start-1 flex items-center gap-0.5"
          classList={{ exit: Boolean(props.thread) }}
          style={at(props.thread?.at ?? 0)}
        >
          <ViewTab open>{t("live_sessions.tab_activity")}</ViewTab>
          <ViewTab>{t("marketing.mock.pane.chats")}</ViewTab>
          <span class="ms-auto flex items-center gap-1">
            <span class={ICON}>
              <TbFilter class="size-4" aria-hidden="true" />
            </span>
            <span class={ICON}>
              <TbLayoutSidebarRightCollapse class="size-4" aria-hidden="true" />
            </span>
          </span>
        </div>
        <Show when={props.thread}>
          {(thread) => (
            <div
              class="card-appear col-start-1 row-start-1 flex min-w-0 items-center gap-1.5 bg-panel"
              style={at(thread().at)}
            >
              <span class={ICON}>
                <TbArrowLeft class="size-4" aria-hidden="true" />
              </span>
              <span class="min-w-0 truncate text-sm font-semibold">{thread().title}</span>
              <span class="shrink-0 text-sm text-muted-text">· Chief</span>
              <span class={`${ICON} ms-auto`}>
                <TbX class="size-4" aria-hidden="true" />
              </span>
            </div>
          )}
        </Show>
      </div>
      <div class="relative min-h-0 flex-1 overflow-hidden">{props.children}</div>
      <div class="m-2 mt-0 shrink-0 rounded-lg border border-primary-border bg-tile px-2.5 py-2">
        <p class="text-sm text-muted-text">{t("marketing.mock.pane.ask")}</p>
        <div class="mt-2 flex items-center gap-2 text-xs">
          <span class={`flex items-center gap-1 font-medium ${CHIEF_ACCENT}`}>
            <Crown class="h-auto w-3" />
            @Chief
          </span>
          <TbArrowUp class="ms-auto size-3.5 text-muted-text" aria-hidden="true" />
        </div>
      </div>
    </div>
  );
};

// Brand blue is too dark to read as small text on the dark theme.
export const CHIEF_ACCENT = "text-brand-text";

const VERB = {
  found: { key: "marketing.mock.pane.intelligence", tone: CHIEF_ACCENT },
  waiting: { key: "marketing.mock.pane.needs_you", tone: "text-(--chief-gold-ink)" },
  did: { key: "marketing.mock.pane.done", tone: "text-(--chief-success-ink)" },
} as const;

/** A section heading in Activity: Intelligence cards, Needs you or Done, with its mark and count. */
export const PaneSection: Component<{ verb: keyof typeof VERB; count: JSX.Element; class?: string }> = (props) => {
  const { t } = useI18n();
  return (
    <div class={`flex items-center gap-1.5 px-2 pt-2 pb-1 text-xs font-semibold ${props.class ?? ""}`}>
      <Switch>
        <Match when={props.verb === "waiting"}>
          <span class="mx-px size-2.5 shrink-0 rounded-full border-2 border-(--chief-gold-ink)" />
        </Match>
        <Match when={props.verb === "did"}>
          <TbCircleCheck class={`size-3.5 shrink-0 ${VERB.did.tone}`} aria-hidden="true" />
        </Match>
        <Match when={props.verb === "found"}>
          <TbMessageCircleQuestion class={`size-3.5 shrink-0 ${CHIEF_ACCENT}`} aria-hidden="true" />
        </Match>
      </Switch>
      <span class={VERB[props.verb].tone}>{t(VERB[props.verb].key)}</span>
      <span class="font-normal text-muted-text tabular-nums">{props.count}</span>
    </div>
  );
};

// An offer card's buttons: its named action is the ghost, an approval the brand
// push.
export const GHOST_BUTTON =
  "inline-flex h-6 shrink-0 items-center gap-1.5 rounded-md bg-tile px-2.5 text-xs whitespace-nowrap text-secondary-text ring-1 ring-primary-border ring-inset";
const PUSH_BUTTON =
  "mac-control mac-push mac-push-brand inline-flex shrink-0 items-center gap-1.5 px-2.5 text-xs whitespace-nowrap";

/** A tool's group under Needs you: the gold box, the tool's mark, what it makes and how many wait. */
export const NeedsGroup: ParentComponent<{
  icon: JSX.Element;
  label: string;
  count: number;
  class?: string;
  style?: JSX.CSSProperties;
}> = (props) => (
  <div
    class={`flex flex-col gap-1.5 rounded-lg border border-accent/40 bg-accent/5 p-1.5 ${props.class ?? ""}`}
    style={props.style}
  >
    <div class="flex items-center gap-1.5 px-1 pt-0.5 text-xs">
      <span class="grid size-5 place-items-center rounded-full bg-accent/20">{props.icon}</span>
      <span class="font-semibold text-primary-text">{props.label}</span>
      <span class="text-muted-text tabular-nums">· {props.count}</span>
    </div>
    {props.children}
  </div>
);

/** A card under Needs you: up to two lines, what it rests on, and its foot, Not now beside the one named action. */
export const NeedsCard: ParentComponent<{ title: string; meta?: string; foot: JSX.Element }> = (props) => (
  <div class="rounded-md bg-tile px-2.5 pt-2 pb-1.5 ring-1 ring-primary-border">
    <p class="line-clamp-2 text-sm leading-snug text-primary-text">{props.title}</p>
    <Show when={props.meta}>
      <p class="mt-0.5 text-xs text-muted-text">{props.meta}</p>
    </Show>
    {props.children}
    <div class="mt-1.5 flex min-h-7 items-center justify-end gap-3">{props.foot}</div>
  </div>
);

/** A one-line row in Activity: its mark, what it is, and what sits at its end. */
export const PANE_ROW =
  "grid min-h-[34px] grid-cols-[18px_minmax(0,1fr)_auto] items-center gap-2 rounded-md px-2 text-sm";

/** A row whose work is still landing: the spinner, what it is, and where it is being added. */
export const WorkingRow: Component<{ title: string; status: JSX.Element }> = (props) => (
  <div class={PANE_ROW}>
    <ProcessingSpinner class="size-3 border-brand border-t-transparent" />
    <span class="truncate text-primary-text">{props.title}</span>
    <span class="flex items-center gap-1 text-xs text-brand">{props.status}</span>
  </div>
);

/** A row under Done: the green check, what landed, and where, with the link to it. */
export const DoneRow: Component<{ title: string; where: string }> = (props) => (
  <div class={PANE_ROW}>
    <TbCircleCheck class="size-3.5 text-(--chief-success-ink)" aria-hidden="true" />
    <span class="truncate text-secondary-text">{props.title}</span>
    <span class="flex items-center gap-1 text-xs text-(--chief-success-ink)">
      {props.where}
      <TbExternalLink class="size-3" aria-hidden="true" />
    </span>
  </div>
);

/** A document open in the Documents tab: the way back to the list over the document itself. */
export const OpenDocument: ParentComponent = (props) => {
  const { t } = useI18n();
  return (
    <div class="flex h-full flex-col">
      <span class="mx-2 mt-1.5 flex h-6 w-fit shrink-0 items-center gap-1 rounded-md px-2 text-xs text-muted-text">
        <TbArrowLeft class="size-3.5" aria-hidden="true" />
        {t("live_sessions.tabs.documents")}
      </span>
      <div class="relative min-h-0 flex-1">{props.children}</div>
    </div>
  );
};

/** A collaborator's caret in a shared document: a bar in their colour with their name over it. */
export const Caret: Component<{ person: Person }> = (props) => (
  <span
    class="relative -mx-px inline-block h-[1.1em] translate-y-0.5 border-x"
    style={{ "border-color": props.person.color }}
  >
    <span class="mk-caret-label" style={{ "background-color": props.person.color }}>
      {props.person.name}
    </span>
  </span>
);

/** The foot of a card waiting on an approval: Needs your OK beside Approve, which `pressAt` presses under `cursor`. */
export const ApproveFoot: Component<{ pressAt?: number; cursor?: JSX.Element }> = (props) => {
  const { t } = useI18n();
  return (
    <span class="flex items-center gap-2">
      <span class="text-xs font-medium text-(--chief-gold-ink)">{t("marketing.mock.offer.needs_ok")}</span>
      <span class="relative" classList={{ press: props.pressAt !== undefined }} style={at(props.pressAt ?? 0)}>
        <span class={PUSH_BUTTON}>
          <TbCheck class="size-3.5 shrink-0" aria-hidden="true" />
          {t("marketing.mock.approve")}
        </span>
        {props.cursor}
      </span>
    </span>
  );
};

/** The foot of an offer card: Not now beside its one named action, which `pressAt` presses under `cursor`. */
export const OfferFoot: Component<{ icon: JSX.Element; label: string; pressAt?: number; cursor?: JSX.Element }> = (
  props
) => {
  const { t } = useI18n();
  return (
    <span class="flex items-center gap-3">
      <span class="text-xs text-muted-text">{t("marketing.mock.activity.not_now")}</span>
      <span class="relative" classList={{ press: props.pressAt !== undefined }} style={at(props.pressAt ?? 0)}>
        <span class={GHOST_BUTTON}>
          {props.icon}
          {props.label}
        </span>
        {props.cursor}
      </span>
    </span>
  );
};

/**
 * A question to Chief and its answer, as the chat draws them: the question in
 * its bordered bubble, the answer under Chief's label, and the sources under
 * that, which land at `sourcesAt`.
 */
export const ChatExchange: Component<{
  question: string;
  answer: JSX.Element;
  sources: { icon: JSX.Element; label: string }[];
  sourcesAt?: number;
}> = (props) => {
  const { t } = useI18n();
  return (
    <div class="flex flex-col gap-3">
      <GradientBorder rounded="rounded-2xl" bgColor="bg-panel">
        <div class="px-5 py-3.5">
          <span class="text-sm text-secondary-text">{t("common.you")}</span>
          <p class="mt-2 text-sm leading-relaxed text-primary-text">{props.question}</p>
        </div>
      </GradientBorder>
      <div class="flex flex-col gap-1">
        <span class="text-[0.625rem] font-semibold tracking-[0.04em] text-muted-text uppercase">
          {t("marketing.mock.roundtrip.by_chief")}
        </span>
        <p class="text-sm leading-relaxed text-primary-text">{props.answer}</p>
        <div
          class="mt-2 flex flex-wrap items-center gap-1.5"
          classList={{ "card-appear": props.sourcesAt !== undefined }}
          style={at(props.sourcesAt ?? 0)}
        >
          <For each={props.sources}>
            {(source) => (
              <span class="inline-flex items-center gap-1.5 rounded-md border border-primary-border bg-panel px-2 py-0.5 text-xs text-secondary-text">
                {source.icon}
                {source.label}
              </span>
            )}
          </For>
        </div>
      </div>
    </div>
  );
};
